import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { MenuOptionValue, Paginated, PaginationQuery } from "../../types";
import useApi, { buildUrl } from "../api";
import { CustomError } from "../errors";
import { DeleteItemResponseBody, InsertItem, Item } from "./types";

const useItemApi = () => {
  const baseurl = "/items";

  const api = useApi();

  const searchItems = useCallback(
    async (query: PaginationQuery, signal?: AbortSignal, isKit?: boolean) => {
      const url = isKit ? buildUrl(`${baseurl}?isKit=${isKit}`, query) : buildUrl(baseurl, query);
      const x = await api.get<Paginated<Item>>({ url, signal });
      return x.items;
    },
    [api],
  );

  const useItem = useCallback(
    (itemID: string) => {
      const url = `${baseurl}/${itemID}`;
      const result = useQuery<Item | null, CustomError>({
        queryKey: [url],
        queryFn: ({ signal }) => api.get<Item>({ url, signal }),
        enabled: false,
      });
      return { ...result, error: result.error ?? undefined };
    },
    [api],
  );

  const useItems = useCallback(
    (query: PaginationQuery) => {
      const url = buildUrl(baseurl, query);
      const result = useQuery<Paginated<Item>, CustomError>({
        queryKey: [url],
        queryFn: ({ signal }) => api.get<Paginated<Item>>({ url, signal }),
      });
      return { ...result, error: result.error ?? undefined };
    },
    [api],
  );

  const getFieldFiltersQuery = useCallback(
    (fieldName: string) => {
      const url = `${baseurl}/filters?field=${fieldName}`;
      return {
        queryKey: [url],
        queryFn: ({ signal }: { signal: AbortSignal }) => api.get<MenuOptionValue[]>({ url, signal }),
      };
    },
    [api],
  );

  const syncItem = useCallback(
    async (itemID: string) => {
      const url = baseurl + "/" + itemID + "/sync";
      const data = await api.post<null, Item>(url);
      api.mutateCache([baseurl]);
      return data;
    },
    [api],
  );

  const createItem = useCallback(
    async (item: InsertItem) => {
      const data = await api.post<InsertItem, Item>(baseurl, item);
      api.mutateCache([baseurl]);
      return data;
    },
    [api],
  );

  const deleteItem = useCallback(
    async (itemID: string, deleteComponents: boolean = false): Promise<boolean> => {
      const url = `${baseurl}/${itemID}?deleteComponents=${deleteComponents}`;
      const data = await api.delWithResult<DeleteItemResponseBody>(url);
      api.mutateCache([baseurl]);
      return Promise.resolve(!!data.allComponentsDeleted);
    },
    [api],
  );

  return {
    searchItems,
    useItems,
    getFieldFiltersQuery,
    createItem,
    deleteItem,
    syncItem,
    useItem,
  };
};

export default useItemApi;
